import { AUTH_CONST } from '@/constant';
import {
  type ApiError,
  type BaseApiResponse,
  emitter,
  storage,
  uuid,
} from '@/package';
import { axiosInstance } from '@/util';

const createRefreshProcessor = <
  D extends BaseApiResponse<any> = BaseApiResponse<any>,
  T extends ApiError<D, any> = ApiError<D, any>,
>() => {
  const event = emitter;
  const failedIdList = [] as string[];
  const retryRefreshCounter = {
    value: 0,
    increase: () => retryRefreshCounter.value++,
    initCount: () => (retryRefreshCounter.value = 0),
    isOver: () => retryRefreshCounter.value === 4,
    over() {
      this.initCount();

      event.emit('signOut');
      retryRequest(false);
      isRefreshing = false;
    },
  };
  let isRefreshing = false;

  const pushAndWait = (resErr: T) => {
    const id = uuid();

    failedIdList.push(id);

    return new Promise<D>((resolve, reject) => {
      emitter.once(id, (successfulRefresh: boolean) => {
        if (!successfulRefresh) {
          reject(resErr);
          return;
        }

        const config = resErr.config!;

        if (config.headers?.Authorization) {
          const idToken = storage.local.get<string>(
            AUTH_CONST.KEYS.STORAGE_ID_TOKEN,
          );

          config.headers.Authorization = `Bearer ${idToken}`;
        }

        axiosInstance(config)
          .then(res => res as unknown as D)
          .then(resolve)
          .catch(reject);
      });
    });
  };

  const retryRequest = (successfulRefresh: boolean) => {
    let id: Nullish<string>;

    while ((id = failedIdList.pop()!)) {
      emitter.emit(id, successfulRefresh);
    }
  };

  const callRefresh = () => {
    event.emit('refresh');

    return !isRefreshing
      ? new Promise<void>((resolve, reject) => {
          isRefreshing = true;

          event.on('refreshRes', data => {
            if (data instanceof Error) {
              if (!data.cause) {
                retryRefreshCounter.over();

                reject(data);
              }

              const { url, status, statusText } = data.cause as any;

              runRefresh({
                config: { url },
                status,
                statusText,
              } as T);
            } else {
              isRefreshing = false;

              resolve();
            }
          });
        })
      : Promise.reject(new Error('Already call refresh'));
  };

  const runRefresh = (err: T) => {
    const isRefreshFailed = !!err.config?.url?.match(/refresh/);

    if (isRefreshFailed) {
      if (retryRefreshCounter.isOver()) {
        retryRefreshCounter.over();

        return Promise.reject<T>(err);
      }

      retryRefreshCounter.increase();

      return callRefresh();
    }

    if (err.status === 401 && err.data.resultCode === '3401' && err.config) {
      const waitingRetryReq = pushAndWait(err);

      return isRefreshing
        ? waitingRetryReq
        : callRefresh().then(() => {
            retryRequest(true);

            return waitingRetryReq;
          });
    }

    return Promise.reject<T>(err);
  };

  return {
    event,
    runRefresh,
  };
};

export default createRefreshProcessor;
